<script setup lang="ts">
/**
 * Who is signed in, and the two controls that belong to them.
 *
 * It sits at the foot of the rail rather than in a top bar, because there is no
 * top bar. Identity, appearance and sign-out are account-level rather than
 * screen-level, and the rail is the chrome that is always on screen.
 *
 * Three variants, because the rail has three shapes and this is the part of it
 * with the least room:
 *
 *   | `rail`      | 1280+, or expanded: name, org, switch and a labeled button |
 *   | `collapsed` | the icon-only rail: one 44px column, everything stacked    |
 *   | `bar`       | below 768: a row in the phone top bar, beside the brand    |
 *
 * The identity text is visually hidden in the two tight variants, never
 * dropped: the avatar is two initials, which is a shorthand for a name a screen
 * reader still has to be able to read. So the initials are `aria-hidden` and the
 * name they stand for is not.
 *
 * **Sign-out asks first.** In the two tight variants it is a 44px glyph at the
 * foot of the rail, a thumb's width from the navigation, and on the phone bar it
 * is the last thing before the edge of the screen. Nothing is destroyed by
 * signing out, but the cost of a misfire is a password and whatever was on the
 * screen, which is more than a second click costs. `aria-haspopup="dialog"`
 * tells a screen reader that before the press, not after it.
 */

import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import ThemeMenu from './ThemeMenu.vue'

import MpConfirmDialog from '@/components/base/MpConfirmDialog.vue'
import MpIcon from '@/components/base/MpIcon.vue'
import { ROLE_LABELS } from '@/lib/auth/contract'
import { useSessionStore } from '@/stores/session'

const props = defineProps<{ variant: 'rail' | 'collapsed' | 'bar' }>()

const session = useSessionStore()
const router = useRouter()
const queryClient = useQueryClient()

/** Only the widest variant has room for a word next to a glyph. */
const showLabels = computed(() => props.variant === 'rail')

/**
 * First and last initial. Two letters at most — a wall of initials is not a
 * shorthand, and a single-word display name has only one to give.
 */
const initials = computed(() => {
  const parts = (session.session?.displayName ?? '').split(/\s+/).filter(part => part.length > 0)
  if (parts.length === 0) return '?'
  const letters = parts.length === 1 ? [parts[0]![0]!] : [parts[0]![0]!, parts[parts.length - 1]![0]!]
  return letters.join('').toUpperCase()
})

const confirming = ref(false)

/**
 * Clearing the store is not signing out: guards only run on navigation, so
 * without the push the user stays on the authenticated screen, rendering
 * whatever the query cache still holds. Navigate first, then drop the cache —
 * query keys are not scoped by account, so anything left in it would be served
 * to the next account that signs in on this browser. Clearing before the push
 * would instead refetch the still-mounted screen's queries with no session.
 */
async function signOut (): Promise<void> {
  confirming.value = false
  await session.signOut()
  await router.push({ name: 'login' })
  queryClient.clear()
}
</script>

<template>
  <div
    class="mp-account"
    :class="`mp-account--${props.variant}`"
  >
    <div
      v-if="session.session"
      class="mp-account__who"
    >
      <span
        class="mp-account__avatar"
        aria-hidden="true"
      >{{ initials }}</span>
      <span :class="showLabels ? 'mp-account__identity' : 'mp-sr-only'">
        <span class="mp-account__name">{{ session.session.displayName }}</span>
        <span class="mp-account__org">{{ session.session.orgName }} · {{ ROLE_LABELS[session.session.role] }}</span>
      </span>
    </div>

    <div class="mp-account__controls">
      <ThemeMenu
        tone="nav"
        :orientation="props.variant === 'collapsed' ? 'vertical' : 'horizontal'"
      />
      <button
        class="mp-account__signout mp-hit"
        type="button"
        aria-haspopup="dialog"
        @click="confirming = true"
      >
        <!-- The glyph stands in for the word only where there is no room for
             the word. Both at once is a redundancy the rail cannot afford. -->
        <MpIcon
          v-if="!showLabels"
          name="signout"
          size="sm"
        />
        <span :class="showLabels ? undefined : 'mp-sr-only'">Sign out</span>
      </button>
    </div>

    <MpConfirmDialog
      :open="confirming"
      title="Sign out?"
      confirm-label="Sign out"
      @confirm="signOut"
      @cancel="confirming = false"
    >
      Anything you have not saved on this screen will be lost, and you will need
      your email and password to get back in.
    </MpConfirmDialog>
  </div>
</template>

<style scoped>
.mp-account {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  color: var(--text-on-nav);
}

/* Boxes in the rail align on the rail's own padding; the glyphs and text
   inside them align one step further in, where the nav link's own padding puts
   its icon. The avatar is a glyph, so it takes the extra step — and the row of
   controls below does not, because a pill and a button are boxes. */
.mp-account__who {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-block-size: var(--target-min);
  padding-inline: var(--space-3);
}

.mp-account__avatar {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--space-8);
  block-size: var(--space-8);
  border-radius: var(--radius-pill);
  background: color-mix(in srgb, var(--text-on-nav) 22%, transparent);
  font-family: var(--font-body);
  font-size: var(--font-size-caption);
  font-weight: 700;
  letter-spacing: 0.04em;
}

.mp-account__identity {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
}

/* The rail is a fixed width and a display name is not, so the name truncates
   rather than widening the rail or wrapping to three lines. The full string is
   still the element's text, so a screen reader reads all of it. */
.mp-account__name,
.mp-account__org {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp-account__name {
  font-size: var(--font-size-small);
  font-weight: 600;
}

.mp-account__org {
  font-size: var(--font-size-caption);
  opacity: 0.75;
}

.mp-account__controls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* A button, not a nav link — it does something rather than going somewhere, and
   the rail's link treatment made it read as an eighth destination. Deliberately
   not an `MpButton`: every variant in DESIGN.md section 9 is drawn for a light
   surface, so `secondary` here is a white pill and the loudest thing in the
   chrome, and `ghost` is navy text on navy. This is the same outline shape at
   nav polarity. */
.mp-account__signout {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-block-size: var(--control-h);
  padding-inline: var(--space-3);
  border: 1px solid color-mix(in srgb, var(--text-on-nav) 30%, transparent);
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--text-on-nav) 8%, transparent);
  color: var(--text-on-nav);
  font-family: var(--font-body);
  font-size: var(--font-size-small);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--motion-ease);
}

.mp-account__signout:hover {
  background: color-mix(in srgb, var(--text-on-nav) 18%, transparent);
}

.mp-account__signout:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* ── The icon-only rail: one column, 44px wide ──────────────────────────── */

.mp-account--collapsed,
.mp-account--collapsed .mp-account__controls {
  flex-direction: column;
  align-items: center;
}

.mp-account--collapsed .mp-account__who,
.mp-account--bar .mp-account__who {
  justify-content: center;
  padding-inline: 0;
}

.mp-account--collapsed .mp-account__signout {
  flex: none;
  inline-size: var(--target-min);
  block-size: var(--target-min);
  padding-inline: 0;
}

/* ── The phone top bar ──────────────────────────────────────────────────── */

.mp-account--bar {
  flex-direction: row;
  align-items: center;
}

.mp-account--bar .mp-account__signout {
  flex: none;
  inline-size: var(--target-min);
  padding-inline: 0;
}
</style>
